import { useEffect, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { LoadScreen } from "@/core/components/shared";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSessionStore } from "@/core/stores/session.store";
import type { OtpChannel } from "@/features/auth/api/dto";
import { OtpChallenge } from "@/features/auth/components";

/** Extrait un claim string du payload JWT (userInfo persisté). */
function pickClaim(
  claims: Record<string, unknown> | null,
  key: string
): string | null {
  const value = claims?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * Gate OTP post-login : l'utilisateur est authentifié (jeton présent) mais ne
 * peut pas accéder au dashboard tant qu'il n'a pas validé un code reçu sur son
 * e-mail (claim `email` du JWT) ou son téléphone (fallback `phone_number`).
 *
 * Validé → `setOtpVerified(true)` + navigation `/app`. Non-authentifié ou déjà
 * vérifié → redirections symétriques.
 */
export function VerifyOtpPage() {
  const { t } = useTypedTranslation();
  const tx = (key: string) => t(key as never, {} as never) as string;
  const navigate = useNavigate();

  const accessToken = useSessionStore((state) => state.accessToken);
  const otpVerified = useSessionStore((state) => state.otpVerified);
  const userInfo = useSessionStore((state) => state.userInfo);
  const setOtpVerified = useSessionStore((state) => state.setOtpVerified);

  const { recipientAddress, channel } = useMemo(() => {
    const email = pickClaim(userInfo, "email");
    const phone = pickClaim(userInfo, "phone_number");
    if (email) return { recipientAddress: email, channel: "EMAIL" as OtpChannel };
    if (phone) return { recipientAddress: phone, channel: "SMS" as OtpChannel };
    return { recipientAddress: "", channel: "EMAIL" as OtpChannel };
  }, [userInfo]);

  // Session rompue entre temps → retour au login (RequireAuth gérerait aussi).
  useEffect(() => {
    if (!accessToken) navigate("/login", { replace: true });
  }, [accessToken, navigate]);

  if (!accessToken) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <LoadScreen />
      </div>
    );
  }

  // Déjà vérifié : accès direct au dashboard (anti double-prompt).
  if (otpVerified) return <Navigate to="/app" replace />;

  if (!recipientAddress) {
    // Pas d'adresse de contact dans le jeton — on ne peut pas envoyer de code.
    // On débloque pour ne pas verrouiller l'utilisateur ; côté serveur, un
    // mapper audience/email devrait être posé (voir AGENTS.md §Auth).
    setOtpVerified(true);
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md flex flex-col gap-4">
        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-2xl font-semibold text-foreground">
            {tx("auth.otp.gate.pageTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {tx("auth.otp.gate.pageSubtitle")}
          </p>
        </div>

        <OtpChallenge
          channel={channel}
          recipientAddress={recipientAddress}
          preferredLanguage="FR"
          partyId={pickClaim(userInfo, "partyId")}
          keycloakUserId={pickClaim(userInfo, "sub")}
          onVerified={() => {
            setOtpVerified(true);
            navigate("/app", { replace: true });
          }}
        />
      </div>
    </div>
  );
}
